import React from 'react'
import { Container, ContainerProps, Box } from '@mui/material'

interface ResponsiveContainerProps extends Omit<ContainerProps, 'maxWidth'> {
  children: React.ReactNode
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false
  padding?: number | { xs?: number; sm?: number; md?: number; lg?: number; xl?: number }
  fullHeight?: boolean
  mobileOptimized?: boolean
}

const ResponsiveContainer: React.FC<ResponsiveContainerProps> = ({
  children,
  maxWidth = 'xl',
  padding = 3,
  fullHeight = false,
  mobileOptimized = true,
  sx,
  ...props
}) => {
  const getPadding = () => {
    if (typeof padding === 'number') {
      // Mobile-optimized default padding
      const mobilePadding = mobileOptimized ? Math.max(1, padding - 1) : padding
      return { 
        xs: mobilePadding, 
        sm: padding, 
        md: padding, 
        lg: padding, 
        xl: padding 
      }
    }
    return {
      xs: padding.xs ?? (mobileOptimized ? 1 : 2),
      sm: padding.sm ?? 2,
      md: padding.md ?? 3,
      lg: padding.lg ?? 3,
      xl: padding.xl ?? 3,
    }
  }

  const paddingValues = getPadding()

  return (
    <Container
      maxWidth={maxWidth}
      sx={{
        py: paddingValues,
        px: paddingValues,
        ...(fullHeight && {
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }),
        // Mobile-specific optimizations
        ...(mobileOptimized && {
          '@media (max-width: 600px)': {
            px: 1,
            py: 1,
            pb: 4, // Increased bottom padding on mobile for better content visibility
            // Ensure content doesn't get hidden behind bottom navigation
            marginBottom: 0,
          },
        }),
        ...sx,
      }}
      {...props}
    >
      {fullHeight ? (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {children}
        </Box>
      ) : (
        children
      )}
    </Container>
  )
}

export default ResponsiveContainer